import React, { FC, memo, useEffect, useMemo, useRef } from 'react';
import { isRpcStateReady, RpcState, RpcStateReady, RpcStateStatus, useRpcState } from '@om-fe/widgets/dist/common/rpc/rpc-state.hooks'
import { MatchModel } from '@om-fe/models';
import { breadcrumbsBem, SCROLL_INTO_VIEW_OPTIONS } from './breadcrumbs.constants';
import { GetBreadcrumbsResponse } from '../../common/breadcrumbsResponse';
import getConfig, { OMFE2ConfigModel } from '@om-fe/core/utils/config';

interface EventBreadcrumbsPublicProps {
    readonly lang: string;
    readonly disciplineId: string;
    readonly locationId: string;
    readonly tournamentId: string;
    readonly eventId: string;
}

export type EventBreadcrumbsProps = EventBreadcrumbsPublicProps;

export const EventBreadcrumbs: FC<EventBreadcrumbsProps> = memo(function EventBreadcrumbsComponent(
    {
        lang,
        disciplineId,
        locationId,
        tournamentId,
        eventId,
    }: EventBreadcrumbsProps,
) {
    const params = useMemo(() => {
        return { sportId: disciplineId, lang, venueId: locationId, tournamentId, matchId: eventId };
    }, [disciplineId, eventId, lang, locationId, tournamentId]);
    const [response] = useRpcState<GetBreadcrumbsResponse<MatchModel>>('matches', params);
    const ref: React.RefObject<HTMLSpanElement> = useRef<HTMLSpanElement>();
    const config: OMFE2ConfigModel = getConfig();
    const { status }: RpcState<GetBreadcrumbsResponse<MatchModel>> = response;

    useEffect(() => {
        if (status === RpcStateStatus.Success && ref.current) {
            if (!config.virtualState) {
                ref.current.scrollIntoView(SCROLL_INTO_VIEW_OPTIONS);
            }
        }
    }, [status]);

    if (!isRpcStateReady(response)) {
        return null;
    }

    const { data }: RpcStateReady<GetBreadcrumbsResponse<MatchModel>> = response;

    const homeName: string | undefined = data.records[0]?.homeParticipantName || 'Home';
    const awayName: string | undefined = data.records[0]?.awayParticipantName || 'Away';

    return (
        <span ref={ref} className={breadcrumbsBem('CurrentItem', 'EventName')}>
            {homeName} vs {awayName}
        </span>
    );
});
